import React, { useState, useEffect, useRef } from 'react';
import { katalogApi } from '../../api/katalogApi';
import { Search, ChevronDown, Package } from '../../icons/wp-icons';

export default function KatalogAutocomplete({ onSelect }) {
  const [positionen, setPositionen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterKategorie, setFilterKategorie] = useState('');
  const wrapperRef = useRef(null);

  const KATEGORIEN = [
    'Glas',
    'Rahmen',
    'Montage',
    'Fahrt',
    'Material',
    'Bauteile',
    'Kleinteile/Zubehör',
    'Dichtungen/Kleber',
    'Werkzeug/Geräte',
    'Arbeitszeit',
    'Fahrtkosten',
    'Entsorgung',
    'Fremdleistung',
    'Sonstiges'
  ];

  // Alle Positionen laden (inkl. inaktive).
  // Why: KI-Agent nennt dem Kunden nur aktive Positionen (siehe searchCatalog/
  // calculatePrice in toolExecuteService.js), aber bei manueller KVA/Rechnung/
  // Dauerauftrag soll der Handwerker auch inaktive Positionen wählen können
  // (z.B. für Gutschriften oder Nachträge zu alten Aufträgen).
  useEffect(() => {
    loadPositionen();
  }, []);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPositionen = async () => {
    try {
      setLoading(true);
      const data = await katalogApi.getPositionen();
      setPositionen(data);
    } catch (error) {
      console.error('Fehler beim Laden des Katalogs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Gefilterte Positionen basierend auf Suche und Kategorie
  const gefiltertePositionen = positionen.filter(pos => {
    const suchText = search.toLowerCase();
    const matchSearch = !search ||
      pos.bezeichnung?.toLowerCase().includes(suchText) ||
      pos.artikelnummer?.toLowerCase().includes(suchText) ||
      pos.beschreibung?.toLowerCase().includes(suchText);

    const matchKategorie = !filterKategorie || pos.kategorie === filterKategorie;

    return matchSearch && matchKategorie;
  });

  // Nach Kategorien gruppieren
  const gruppiertNachKategorie = KATEGORIEN.reduce((acc, kategorie) => {
    const items = gefiltertePositionen.filter(pos => pos.kategorie === kategorie);
    if (items.length > 0) {
      acc[kategorie] = items;
    }
    return acc;
  }, {});

  const handleSelect = (position) => {
    onSelect(position);
    setSearch('');
    setShowDropdown(false);
    setFilterKategorie('');
  };

  const getKategorieColor = (kategorie) => {
    const colors = {
      'Glas': 'bg-blue-100 text-blue-800',
      'Rahmen': 'bg-amber-100 text-amber-800',
      'Montage': 'bg-green-100 text-green-800',
      'Fahrt': 'bg-purple-100 text-purple-800',
      'Material': 'bg-orange-100 text-orange-800',
      'Bauteile': 'bg-cyan-100 text-cyan-800',
      'Kleinteile/Zubehör': 'bg-teal-100 text-teal-800',
      'Dichtungen/Kleber': 'bg-indigo-100 text-indigo-800',
      'Werkzeug/Geräte': 'bg-rose-100 text-rose-800',
      'Arbeitszeit': 'bg-lime-100 text-lime-800',
      'Fahrtkosten': 'bg-violet-100 text-violet-800',
      'Entsorgung': 'bg-red-100 text-red-800',
      'Fremdleistung': 'bg-sky-100 text-sky-800',
      'Sonstiges': 'bg-gray-100 text-gray-800'
    };
    return colors[kategorie] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Suchfeld mit Dropdown-Button */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Aus Katalog wählen..."
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#057867] focus:border-transparent"
        />
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown Liste */}
      {showDropdown && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Kategorie-Filter */}
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => setFilterKategorie('')}
                className={`px-2 py-1 text-xs rounded-full transition ${
                  !filterKategorie
                    ? 'bg-primary text-white'
                    : 'bg-primary-light text-primary border border-primary/30 hover:bg-primary hover:text-white'
                }`}
              >
                Alle
              </button>
              {KATEGORIEN.map(kat => (
                <button
                  key={kat}
                  type="button"
                  onClick={() => setFilterKategorie(filterKategorie === kat ? '' : kat)}
                  className={`px-2 py-1 text-xs rounded-full transition ${
                    filterKategorie === kat
                      ? 'bg-primary text-white'
                      : 'bg-primary-light text-primary border border-primary/30 hover:bg-primary hover:text-white'
                  }`}
                >
                  {kat}
                </button>
              ))}
            </div>
          </div>

          {/* Liste */}
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-6 text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#057867] mx-auto mb-2"></div>
                Lädt Katalog...
              </div>
            ) : gefiltertePositionen.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                {search ? 'Keine Positionen gefunden' : 'Keine Katalog-Positionen vorhanden'}
              </div>
            ) : filterKategorie ? (
              // Ungegruppierte Ansicht wenn Kategorie gefiltert
              gefiltertePositionen.map(pos => (
                <button
                  key={pos.id}
                  type="button"
                  onClick={() => handleSelect(pos)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition ${!pos.aktiv ? 'bg-gray-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium truncate ${pos.aktiv ? 'text-gray-900' : 'text-gray-400'}`}>{pos.bezeichnung}</p>
                        {pos.artikelnummer && (
                          <span className="text-xs text-gray-400">#{pos.artikelnummer}</span>
                        )}
                        {!pos.aktiv && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-600 uppercase tracking-wide">
                            inaktiv
                          </span>
                        )}
                      </div>
                      {pos.beschreibung && (
                        <p className={`text-sm truncate ${pos.aktiv ? 'text-gray-500' : 'text-gray-400'}`}>{pos.beschreibung}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {pos.einheit} • {pos.mwstSatz}% MwSt
                      </p>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      <p className={`font-semibold ${pos.aktiv ? 'text-[#057867]' : 'text-gray-400'}`}>
                        {pos.verkaufspreis.toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              // Gruppierte Ansicht nach Kategorie
              Object.entries(gruppiertNachKategorie).map(([kategorie, items]) => (
                <div key={kategorie}>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 sticky top-0">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getKategorieColor(kategorie)}`}>
                      {kategorie}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">
                      ({items.length})
                    </span>
                  </div>
                  {items.map(pos => (
                    <button
                      key={pos.id}
                      type="button"
                      onClick={() => handleSelect(pos)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 transition ${!pos.aktiv ? 'bg-gray-50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-medium truncate ${pos.aktiv ? 'text-gray-900' : 'text-gray-400'}`}>{pos.bezeichnung}</p>
                            {pos.artikelnummer && (
                              <span className="text-xs text-gray-400">#{pos.artikelnummer}</span>
                            )}
                            {!pos.aktiv && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-600 uppercase tracking-wide">
                                inaktiv
                              </span>
                            )}
                          </div>
                          {pos.beschreibung && (
                            <p className={`text-sm truncate ${pos.aktiv ? 'text-gray-500' : 'text-gray-400'}`}>{pos.beschreibung}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {pos.einheit} • {pos.mwstSatz}% MwSt
                          </p>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className={`font-semibold ${pos.aktiv ? 'text-[#057867]' : 'text-gray-400'}`}>
                            {pos.verkaufspreis.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
